import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ExternalLink } from "lucide-react";
import Markdown from "@/components/Markdown";
import type { LibraryEntry } from "@/content/library";
import { categoryName } from "@/store/customContentStore";
import { useLibraryStore } from "@/store/libraryStore";
import { useLibraryContentStore } from "@/store/libraryContentStore";

interface LibraryEntryDrawerProps {
  /** Entry to show; null hides the drawer. */
  entryId: string | null;
  /** Optional related entries to switch between within the drawer. */
  relatedIds?: string[];
  onClose: () => void;
}

/**
 * A slide-in reader for a single Library entry, shown over the current screen so
 * the user never loses their place (e.g. opened from a challenge's "Study this").
 */
export default function LibraryEntryDrawer({ entryId, relatedIds = [], onClose }: LibraryEntryDrawerProps) {
  const getHighlights = useLibraryStore((s) => s.getHighlights);
  const [switchId, setSwitchId] = useState<string | null>(null);

  // Build the by-id map from the library sub-sessions so "Study this" can open
  // any section, including ones the user authored.
  const subSessions = useLibraryContentStore((s) => s.subSessions);
  const effectiveEntries = useLibraryContentStore((s) => s.effectiveEntries);
  const byId = useMemo(() => {
    const map: Record<string, LibraryEntry> = {};
    for (const e of effectiveEntries()) map[e.id] = e;
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subSessions]);

  // Reset the in-drawer selection whenever a new entry is opened.
  useEffect(() => setSwitchId(null), [entryId]);

  // Close on Escape.
  useEffect(() => {
    if (!entryId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [entryId, onClose]);

  const activeId = switchId ?? entryId;
  const entry = activeId ? byId[activeId] : null;
  const related = relatedIds.map((id) => byId[id]).filter(Boolean);

  return (
    <AnimatePresence>
      {entryId && entry && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 320, damping: 34 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l border-border bg-surface shadow-2xl"
          >
            <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted">Study · Library</span>
              <button
                onClick={onClose}
                aria-label="Close"
                className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted transition-colors hover:bg-surface-2 hover:text-text"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {related.length > 1 && (
              <div className="flex flex-wrap gap-1.5 border-b border-border px-5 py-2.5">
                {related.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setSwitchId(e.id)}
                    className={[
                      "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                      e.id === activeId ? "bg-primary text-primary-fg" : "text-muted hover:bg-surface-2 hover:text-text",
                    ].join(" ")}
                  >
                    {e.title}
                  </button>
                ))}
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-6 py-5">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {entry.disciplineIds.map((id) => (
                  <span key={id} className="rounded-full bg-primary/15 px-3 py-1 font-medium text-primary">
                    {categoryName(id)}
                  </span>
                ))}
              </div>
              <h2 className="mt-3 font-display text-2xl font-bold text-text">{entry.title}</h2>
              {entry.summary && <p className="mt-1 text-muted">{entry.summary}</p>}

              <div className="mt-4 border-t border-border pt-2">
                <Markdown source={entry.body} highlights={getHighlights(entry.id)} />
              </div>

              {entry.sources.length > 0 && (
                <div className="mt-6 border-t border-border pt-4">
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted">Sources</div>
                  <ul className="mt-2 space-y-1 text-sm text-muted">
                    {entry.sources.map((s, n) => (
                      <li key={n} className="flex items-start gap-1.5">
                        <span>•</span>
                        <span>
                          <span className="text-text">{s.title}</span>
                          {s.author ? ` — ${s.author}` : ""}
                          {s.url && (
                            <a href={s.url} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-primary underline">
                              link <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
